import React, { useEffect, useMemo, useRef, useState } from 'react';
import TopBar from '../components/layout/TopBar';
import QuickBookingModal from '../components/QuickBookingModal';

// Configuration imports
import { HOMEPAGE_CONFIG, ANIMATION_CONFIG } from '../config/homepageConfig';

// Styles
import '../styles/responsive-utilities.css';

import useHomeFilters from '../hooks/useHomeFilters';
import useHomeCategoryCards from '../hooks/useHomeCategoryCards';
import useHomeNavigationHandlers from '../hooks/useHomeNavigationHandlers';
import { DEMAND_HIGHLIGHTED_SERVICES, getServiceDisplayPrice } from '../config/servicePricingConfig';
import { getHomeCatalog } from '../adapters/serviceCatalogAdapter';

// Component imports
import ServiceCategoryCard from '../components/ServiceCategoryCard/ServiceCategoryCard';
import { ServiceCard, NewServiceCard, SpecialOfferCard } from '../components';
import StatsSection from '../components/StatsSection/StatsSection';
import SearchBar from '../components/SearchBar/SearchBar';
import Footer from '../components/layout/Footer';
import PanchangHighlightsSection from '../components/PanchangHighlightsSection';

// Constants and utilities
import { UI_CONSTANTS } from '../constants/uiConstants';
import { combineClasses } from '../utils/formatters';
import { TOPBAR_CONFIG } from '../config/topbarConfig';
import { normalizeService, normalizeServiceList } from '../config/catalog/normalizedServiceSchema';

const HorizontalAnimatedCards = ({ items, renderCard, autoScroll = true }) => {
  const rowRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCards, setVisibleCards] = useState(4);
  const [activePage, setActivePage] = useState(0);
  const hasItems = Array.isArray(items) && items.length > 0;

  useEffect(() => {
    const resolveVisibleCards = () => {
      if (window.innerWidth >= 1024) return 4;
      if (window.innerWidth >= 768) return 3;
      if (window.innerWidth >= 640) return 2;
      return 1;
    };

    const updateVisibleCards = () => setVisibleCards(resolveVisibleCards());
    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);

    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  const hasScrollableItems = hasItems && items.length > visibleCards;

  const totalPages = useMemo(() => {
    if (!hasScrollableItems) {
      return 1;
    }
    return Math.ceil(items.length / visibleCards);
  }, [hasScrollableItems, items.length, visibleCards]);

  useEffect(() => {
    setActivePage(0);
    if (rowRef.current) {
      rowRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [items.length, visibleCards]);

  useEffect(() => {
    if (!hasScrollableItems || !autoScroll || isPaused || totalPages <= 1) {
      return;
    }

    const timer = setInterval(() => {
      setActivePage((prevPage) => (prevPage + 1) % totalPages);
    }, 3200);

    return () => clearInterval(timer);
  }, [autoScroll, hasScrollableItems, isPaused, totalPages]);

  const pagedItems = useMemo(() => {
    if (!hasItems) {
      return [];
    }

    return Array.from({ length: totalPages }, (_, pageIndex) => {
      const startIndex = pageIndex * visibleCards;
      return items.slice(startIndex, startIndex + visibleCards);
    });
  }, [hasItems, items, totalPages, visibleCards]);

  if (!hasItems) {
    return null;
  }

  if (!hasScrollableItems) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {items.map((item, index) => (
          <div key={item?.id || item?.name || index} className="h-full">
            {renderCard(item)}
          </div>
        ))}
      </div>
    );
  }

  const movePage = (direction) => {
    if (totalPages <= 1) {
      return;
    }

    setActivePage((prevPage) => (prevPage + direction + totalPages) % totalPages);
  };

  if (autoScroll) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${activePage * 100}%)` }}
          >
            {pagedItems.map((pageItems, pageIndex) => (
              <div key={`page-${pageIndex}`} className="w-full shrink-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                  {pageItems.map((item, index) => (
                    <div
                      key={`${item?.id || item?.name || 'item'}-${pageIndex}-${index}`}
                      className="h-full"
                      style={{
                        animation: 'fadeInUp 520ms ease-out',
                        animationDelay: `${index * 80}ms`
                      }}
                    >
                      {renderCard(item)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            {Array.from({ length: totalPages }, (_, pageIndex) => (
              <button
                key={`dot-${pageIndex}`}
                type="button"
                onClick={() => setActivePage(pageIndex)}
                className={combineClasses(
                  'h-2.5 rounded-full transition-all duration-300',
                  activePage === pageIndex
                    ? 'w-7 bg-orange-500'
                    : 'w-2.5 bg-orange-200 hover:bg-orange-300'
                )}
                aria-label={`Go to slide ${pageIndex + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => autoScroll && setIsPaused(true)}
      onMouseLeave={() => autoScroll && setIsPaused(false)}
      onTouchStart={() => autoScroll && setIsPaused(true)}
      onTouchEnd={() => autoScroll && setIsPaused(false)}
    >
      {!autoScroll && (
        <>
          <button
            type="button"
            onClick={() => movePage(-1)}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 items-center justify-center rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-md text-gray-700 hover:bg-white"
            aria-label="Scroll left"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => movePage(1)}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 items-center justify-center rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-md text-gray-700 hover:bg-white"
            aria-label="Scroll right"
          >
            ›
          </button>
        </>
      )}

      <div
        ref={rowRef}
        className={combineClasses(
          'overflow-x-auto scroll-smooth snap-x snap-mandatory',
          autoScroll ? 'scrollbar-hide pb-1' : 'pb-2 md:px-12'
        )}
      >
        <div className="flex gap-3 sm:gap-4 lg:gap-6">
          {items.map((item, index) => (
            <div
              key={item?.id || item?.name || index}
              className="snap-start shrink-0 basis-[82vw] sm:basis-[calc((100%-1rem)/2)] md:basis-[calc((100%-2rem)/3)] lg:basis-[calc((100%-4.5rem)/4)] transition-transform duration-300 hover:-translate-y-1"
            >
              {renderCard(item)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const HomePage = ({ onNavigate }) => {
  const homeCatalog = getHomeCatalog();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState(TOPBAR_CONFIG.LOCATION.defaultText);
  const [showQuickBookingModal, setShowQuickBookingModal] = useState(false);
  const [preBookingData, setPreBookingData] = useState(null);
  const {
    serviceCategories,
    categoriesLoading,
    categoriesError
  } = useHomeCategoryCards(homeCatalog.categories);
  
  // Event handlers
  const handleSearch = (term) => {
    setSearchTerm(term || '');
  };

  const {
    normalizedSearchTerm,
    normalizedCityFilter,
    filteredCategories,
    filteredPopularServices,
    filteredNewServices
  } = useHomeFilters({
    searchTerm,
    selectedCity,
    defaultCityLabel: TOPBAR_CONFIG.LOCATION.defaultText,
    categories: serviceCategories,
    popularServices: homeCatalog.popularServices,
    newServices: homeCatalog.newServices
  });

  const {
    handleCategoryClick,
    handleBookNow,
    handleLearnMore,
    handleSeeAll
  } = useHomeNavigationHandlers({
    onNavigate,
    onPrebook: (bookingData) => {
      setPreBookingData(bookingData);
      setShowQuickBookingModal(true);
    }
  });

  const handlePreBookingContinue = (bookingData) => {
    if (!onNavigate) return;
    onNavigate('booking', { bookingData });
  };

  const requestedServices = useMemo(() => normalizeServiceList(
    DEMAND_HIGHLIGHTED_SERVICES.slice(0, 6).map((item) => ({
      ...item,
      name: item.title,
      price: getServiceDisplayPrice(item),
      originalPrice: getServiceDisplayPrice(item)
    })),
    { servicePage: 'home' }
  ), []);

  const popularServices = useMemo(() => normalizeServiceList(
    filteredPopularServices.map((service) => ({
      ...service,
      price: getServiceDisplayPrice(service),
      originalPrice: getServiceDisplayPrice(service)
    })),
    { servicePage: 'most-booked-services' }
  ), [filteredPopularServices]);

  const newServices = useMemo(() => normalizeServiceList(
    filteredNewServices.map((service) => ({
      ...service,
      price: getServiceDisplayPrice(service),
      originalPrice: getServiceDisplayPrice(service)
    })),
    { servicePage: 'new-services' }
  ), [filteredNewServices]);

  return (
    <div className="min-h-screen bg-white">
      {/* TopBar Component */}
      <TopBar 
        cartCount={HOMEPAGE_CONFIG.DEFAULT_PROPS.cartCount}
        currentLocation={selectedCity}
        onLocationClick={(location) => setSelectedCity(location)}
        onSearch={handleSearch}
        onLogoClick={() => onNavigate && onNavigate('home')}
      />

      {/* Hero Section */}
      <section className={combineClasses(
        HOMEPAGE_CONFIG.HERO.backgroundGradient,
        HOMEPAGE_CONFIG.LAYOUT.containerPadding,
        "py-8 md:py-12",
        "mt-0"
      )}>
        <div className={combineClasses(HOMEPAGE_CONFIG.LAYOUT.maxWidth, "mx-auto")}>
          <div className="text-center">
            <h1 className={combineClasses(
              "text-gray-900 mb-3",
              UI_CONSTANTS.TYPOGRAPHY.HEADING_1,
              "animate-fadeIn"
            )}>
              {HOMEPAGE_CONFIG.HERO.title}
            </h1>
            <p className={combineClasses(
              "text-gray-600 mb-6",
              UI_CONSTANTS.TYPOGRAPHY.HEADING_3,
              "animate-fadeIn"
            )}>
              {HOMEPAGE_CONFIG.HERO.subtitle}
            </p>

            {/* Compact Features Row */}
            {HOMEPAGE_CONFIG.HERO.features && (
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                {HOMEPAGE_CONFIG.HERO.features.map((feature, index) => (
                  <div 
                    key={index}
                    className="group relative bg-white/90 backdrop-blur-sm border border-orange-100 rounded-2xl px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105 hover:border-orange-200"
                  >
                    {/* Elegant gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-50/50 to-amber-50/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <span className="text-sm font-semibold text-gray-800 group-hover:text-orange-700 transition-colors duration-300 tracking-wide">
                        {feature.title}
                      </span>
                    </div>

                    {/* Subtle accent line */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-amber-400 group-hover:w-3/4 transition-all duration-500 rounded-full"></div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Enhanced Search Bar with better styling */}
            <div className="max-w-2xl mx-auto mb-6">
              <SearchBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearch={handleSearch}
                placeholder={HOMEPAGE_CONFIG.HERO.searchPlaceholder}
              />
            </div>

            {/* Stats */}
            {HOMEPAGE_CONFIG.DEFAULT_PROPS.enableStats && (
              <StatsSection stats={homeCatalog.stats} />
            )}
          </div>
        </div>
      </section>

      <PanchangHighlightsSection city={selectedCity} />

      <main className={combineClasses(
        HOMEPAGE_CONFIG.LAYOUT.maxWidth,
        "mx-auto",
        HOMEPAGE_CONFIG.LAYOUT.containerPadding,
        HOMEPAGE_CONFIG.LAYOUT.mainSpacing
      )}>
        {/* Main Service Categories */}
        <section className={HOMEPAGE_CONFIG.LAYOUT.sectionSpacing}>
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className={combineClasses(
              "text-gray-900 mb-3",
              UI_CONSTANTS.TYPOGRAPHY.HEADING_2,
              "font-bold text-3xl lg:text-4xl"
            )}>
              {HOMEPAGE_CONFIG.SECTIONS.CATEGORIES.title}
            </h2>
            <p className={combineClasses(
              "text-gray-600 mb-4 text-lg",
              UI_CONSTANTS.TYPOGRAPHY.BODY,
              "max-w-2xl mx-auto"
            )}>
              {HOMEPAGE_CONFIG.SECTIONS.CATEGORIES.subtitle}
            </p>
            {/* Decorative separator */}
            <div className="flex items-center justify-center mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent w-32"></div>
              <div className="mx-4 text-orange-500 text-2xl">🕉️</div>
              <div className="h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent w-32"></div>
            </div>
          </div>
          
          {/* Enhanced Responsive grid layout */}
          <div className={combineClasses(
            "grid gap-4 lg:gap-6",
            HOMEPAGE_CONFIG.SECTIONS.CATEGORIES.gridConfig.mobile,
            HOMEPAGE_CONFIG.SECTIONS.CATEGORIES.gridConfig.tablet,
            HOMEPAGE_CONFIG.SECTIONS.CATEGORIES.gridConfig.desktop,
            HOMEPAGE_CONFIG.SECTIONS.CATEGORIES.gridConfig.large
          )}>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category, index) => (
                <div
                  key={category.id}
                  className={combineClasses(
                    "group relative h-full",
                    ANIMATION_CONFIG.TRANSITIONS.DEFAULT
                  )}
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.6s ease-out forwards'
                  }}
                >
                  <ServiceCategoryCard
                    category={category}
                    onClick={handleCategoryClick}
                    enhanced={true}
                    className="h-full"
                  />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                {normalizedSearchTerm ? 'No service categories matched your search.' : (categoriesError || 'No service categories available')}
              </div>
            )}
          </div>

          {categoriesLoading && (
            <p className="text-center mt-4 text-xs text-gray-400">Refreshing categories...</p>
          )}

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-gray-500 text-sm mb-4">
              Can't find what you're looking for?
            </p>
            <button
              onClick={() => onNavigate && onNavigate('request-custom-service')}
              className={combineClasses(
                "inline-flex items-center px-6 py-3 text-sm font-medium",
                "text-orange-600 bg-orange-50 border border-orange-200",
                "rounded-full hover:bg-orange-100 hover:border-orange-300",
                "transition-all duration-200 ease-in-out",
                "hover:shadow-md hover:scale-105"
              )}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Request Custom Service
            </button>
          </div>
        </section>

        {/* Most Booked Services */}
        <section className="mb-16">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className={combineClasses(
                "text-gray-900",
                UI_CONSTANTS.TYPOGRAPHY.HEADING_2
              )}>
                Most booked services
              </h2>
              <p className={combineClasses(
                "text-gray-600",
                UI_CONSTANTS.TYPOGRAPHY.BODY
              )}>
                Popular choices by our customers
              </p>
            </div>
            <button 
              onClick={() => handleSeeAll('popular')}
              className="text-orange-600 font-semibold hover:text-orange-700 flex items-center space-x-1"
            >
              <span className="hidden sm:inline">See all</span>
              <span className="sm:hidden">All</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {popularServices.length > 0 ? (
            <HorizontalAnimatedCards
              items={popularServices}
              renderCard={(service) => (
                <ServiceCard
                  service={normalizeService(service, { servicePage: 'most-booked-services' })}
                  onBookNow={(selectedService) => handleBookNow(selectedService, 'most-booked-services')}
                  onLearnMore={(selectedService) => handleLearnMore(selectedService, 'most-booked-services')}
                  enhanced={true}
                  className="h-full"
                />
              )}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              {normalizedSearchTerm || normalizedCityFilter ? 'No popular services matched your filters' : 'No popular services available'}
            </div>
          )}
        </section>

        {/* New and Noteworthy */}
        <section className="mb-16">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className={combineClasses(
                "text-gray-900",
                UI_CONSTANTS.TYPOGRAPHY.HEADING_2
              )}>
                New and noteworthy
              </h2>
              <p className={combineClasses(
                "text-gray-600",
                UI_CONSTANTS.TYPOGRAPHY.BODY
              )}>
                Latest spiritual services and offerings
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium border border-green-200">
                NEW
              </span>
              <button 
                onClick={() => handleSeeAll('new')}
                className="text-blue-600 font-semibold hover:text-blue-700 flex items-center space-x-1"
              >
                <span className="hidden sm:inline">See all</span>
                <span className="sm:hidden">All</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          
          {newServices.length > 0 ? (
            <HorizontalAnimatedCards
              items={newServices}
              renderCard={(service) => (
                <NewServiceCard
                  service={normalizeService(service, { servicePage: 'new-services' })}
                  onBookNow={(selectedService) => handleBookNow(selectedService, 'new-services')}
                  onLearnMore={(selectedService) => handleLearnMore(selectedService, 'new-services')}
                  className="h-full"
                />
              )}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              {normalizedSearchTerm || normalizedCityFilter ? 'No new services matched your filters' : 'No new services available'}
            </div>
          )}
        </section>

        {/* Special Offers */}
        <section className="mb-16">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className={combineClasses(
                "text-gray-900",
                UI_CONSTANTS.TYPOGRAPHY.HEADING_2
              )}>
                Most requested services
              </h2>
              <p className={combineClasses(
                "text-gray-600",
                UI_CONSTANTS.TYPOGRAPHY.BODY
              )}>
                Frequently booked and trending spiritual services
              </p>
            </div>
          </div>

          <HorizontalAnimatedCards
            items={requestedServices}
            renderCard={(service) => (
              <ServiceCard
                service={service}
                onBookNow={(selectedService) => handleBookNow(selectedService, 'home')}
                onLearnMore={(selectedService) => handleLearnMore(selectedService, 'home')}
                enhanced={true}
                className="h-full"
              />
            )}
          />
        </section>

        {/* Special Offers */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className={combineClasses(
              "text-gray-900 mb-3",
              UI_CONSTANTS.TYPOGRAPHY.HEADING_2,
              "font-bold text-3xl lg:text-4xl"
            )}>
              Special Offers & Deals
            </h2>
            <p className={combineClasses(
              "text-gray-600 mb-4 text-lg",
              UI_CONSTANTS.TYPOGRAPHY.BODY,
              "max-w-2xl mx-auto"
            )}>
              Exclusive discounts and limited-time offers on spiritual services
            </p>
            {/* Decorative separator */}
            <div className="flex items-center justify-center mb-8">
              <div className="h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent w-32"></div>
              <div className="mx-4 text-purple-500 text-2xl">🎯</div>
              <div className="h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent w-32"></div>
            </div>
          </div>
          
          {homeCatalog.offers.map((offer) => (
            <SpecialOfferCard
              key={offer.id}
              offer={offer}
              onBookNow={handleBookNow}
            />
          ))}
        </section>
      </main>

      {/* Footer */}
      <Footer onNavigate={onNavigate} />

      <QuickBookingModal
        isOpen={showQuickBookingModal}
        bookingData={preBookingData}
        onClose={() => {
          setShowQuickBookingModal(false);
          setPreBookingData(null);
        }}
        onContinue={handlePreBookingContinue}
      />

    </div>
  );
};

export default HomePage;

